import React, { useState } from "react";
import { PageSource } from "web/react/components/save-for-later-button/save-for-later-button";
import WebToAppCampaignModal from "web/react/components/web-to-app-campaign-modal/web-to-app-campaign-modal";
import useWebToAppCampaign, {
    CampaignOptions,
} from "web/react/hooks/use-web-to-app-campaign/use-web-to-app-campaign";
import { CaptureType } from "web/redux/ducks/customer-capture-overlay";

interface WebToAppCampaignProps {
    contextType: string;
    children: React.ReactNode;
    options?: CampaignOptions;
    beforeOnClick?: (event?) => void;
    afterOnClick?: (any?) => void;
    defaultAction: (event) => void;
    onContinueClick?: (event) => void;
    analyticsEventLabel: string;
    id?: number | string;
    productImageUrl?: string;
    continueOptionHref?: string;
    appDeepLinkPath?: string;
    continueMessage?: string;
    productId?: number | string;
    productUrl?: string;
    pageSource?: PageSource;
}

export default function WebToAppCampaign({
    contextType,
    children,
    options = {},
    beforeOnClick,
    afterOnClick,
    defaultAction,
    onContinueClick,
    analyticsEventLabel,
    productImageUrl,
    id = "",
    continueOptionHref = "",
    appDeepLinkPath,
    continueMessage,
    productId,
    productUrl,
    pageSource,
}: WebToAppCampaignProps): JSX.Element {
    const [hasBeenBlocked, setHasBeenBlocked] = useState<boolean>(false);
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const {
        campaignDetails,
        onLoad,
        onClick: onCtaClick,
        fetchCampaign,
    } = useWebToAppCampaign(
        "block_modal",
        {
            contextType,
            hasBeenBlocked,
            ...options,
        },
        false,
        contextType
    );

    function openModal(): void {
        setModalIsOpen(true);
    }

    function closeModal(): void {
        setModalIsOpen(false);
    }

    function onClick(event): void {
        // we must persist the event so it can be used after the handler has run
        event.preventDefault();
        event.persist?.();

        // run anything, regardless of the campaign decision - useful for sending
        // analytics data related to the wrapped component
        beforeOnClick?.(event);
        fetchCampaign()
            .then((campaignDetailsData) => {
                if (campaignDetailsData) {
                    if (campaignDetailsData?.blockType === "hard_block_after_action") {
                        defaultAction(event);
                        return {
                            blockBeforeAction: false,
                            extraContent: { blockType: "hard_block_after_action" },
                        };
                    } else {
                        openModal();
                        return {
                            blockBeforeAction: true,
                        };
                    }
                }
                defaultAction(event);
                return {};
            })
            .then((data) => {
                if (!data.blockBeforeAction) {
                    afterOnClick?.(data.extraContent || {});
                }
            });
    }

    if (campaignDetails && !hasBeenBlocked) {
        setHasBeenBlocked(true);
        onLoad();
    }

    const captureType: CaptureType | undefined =
        pageSource &&
        ((options.isIcon ? "signup_checkout_save" : `signup_${pageSource}_save`) as CaptureType);

    return (
        <>
            <span onClick={onClick}>{children}</span>
            <WebToAppCampaignModal
                id={id}
                isOpen={modalIsOpen}
                onClose={closeModal}
                campaignDetails={campaignDetails}
                continueOptionHref={continueOptionHref}
                onContinueClick={onContinueClick}
                onCtaClick={onCtaClick}
                analyticsEventLabel={analyticsEventLabel}
                contextType={contextType}
                productImageUrl={productImageUrl}
                appDeepLinkPath={appDeepLinkPath}
                continueMessage={continueMessage}
                productId={productId}
                productUrl={productUrl}
                captureType={captureType} // pageSource = 'pdp' or 'feed'
            />
        </>
    );
}
